import { Dashboard } from '../../models/ApiModels';

export type TargetKey = 'systems' | 'boats' | 'upgrades' | 'ultimate';

export type TargetUnit = '%' | 'KSEK';

export type DashboardTargetMetric = {
  key: TargetKey;
  label: string;
  current: number;
  diff: number;
  target: number;
  unit?: TargetUnit;
  barColor: string;
};

export const TARGET_COLORS: Record<TargetKey, string> = {
  systems: '#BFE5A7',
  boats: '#9BE6EE',
  upgrades: '#FFDC80',
  ultimate: '#F9A385',
};

export const TARGET_VALUES: Record<TargetKey, number> = {
  systems: 2000,
  boats: 50,
  upgrades: 3500,
  ultimate: 40,
};

const toFiniteNumber = (value: unknown) => (typeof value === 'number' && Number.isFinite(value) ? value : 0);

export const clampProgress = (value: number) => Math.min(100, Math.max(0, value));

export const getProgressPercent = (current: number, target: number) => {
  if (target <= 0) return 0;
  return (current / target) * 100;
};

export const buildDashboardTargets = (dashboardData: Dashboard): DashboardTargetMetric[] => {
  const upgradesRawSek = toFiniteNumber(dashboardData.orders.orderRevenueYTD.count);
  const upgradesDiffRawSek = toFiniteNumber(dashboardData.orders.orderRevenueYTD.diff);

  return [
    {
      key: 'systems',
      label: 'Systems',
      current: toFiniteNumber(dashboardData.systems.ytdAmountOfSystems.count),
      diff: toFiniteNumber(dashboardData.systems.ytdAmountOfSystems.diff),
      target: TARGET_VALUES.systems,
      barColor: TARGET_COLORS.systems,
    },
    {
      key: 'boats',
      label: 'Boats',
      current: toFiniteNumber(dashboardData.systems.connectedWithBoatModelPercentage.count),
      diff: toFiniteNumber(dashboardData.systems.connectedWithBoatModelPercentage.diff),
      target: TARGET_VALUES.boats,
      unit: '%',
      barColor: TARGET_COLORS.boats,
    },
    {
      key: 'upgrades',
      label: 'Upgrades',
      current: upgradesRawSek / 1000,
      diff: upgradesDiffRawSek / 1000,
      target: TARGET_VALUES.upgrades,
      unit: 'KSEK',
      barColor: TARGET_COLORS.upgrades,
    },
    {
      key: 'ultimate',
      label: 'Ultimate',
      current: toFiniteNumber(dashboardData.systems.ultimatePercentage.count),
      diff: toFiniteNumber(dashboardData.systems.ultimatePercentage.diff),
      target: TARGET_VALUES.ultimate,
      unit: '%',
      barColor: TARGET_COLORS.ultimate,
    },
  ];
};

export const getFulfilledTargetKeys = (targets: DashboardTargetMetric[]) => {
  return targets
    .filter((target) => getProgressPercent(target.current, target.target) >= 100)
    .map((target) => target.key);
};
